import React, { useCallback, useEffect, useRef, useState } from "react";
import { colors } from "../../styles/design-tokens";

export interface Props {
  onClick?: any;
  onDelete: Function;
  onDeleteConfirm?: Function;
  deleteComponent?: React.ReactNode;
  disabled?: boolean;
  height?: number;
  transitionDuration?: number;
  deleteWidth?: number;
  deleteThreshold?: number;
  showDeleteAction?: boolean;
  deleteColor?: string;
  deleteText?: string;
  className?: string;
  id?: string;
  rtl?: boolean;
  children?: React.ReactNode;
}

const cursorPosition = (event: any) => {
  if (event?.touches?.[0]?.clientX) return event.touches[0].clientX;
  if (event?.clientX) return event?.clientX;
  if (event?.nativeEvent?.touches?.[0]?.clientX) return event.nativeEvent.touches[0].clientX;
  return event?.nativeEvent?.clientX;
};

const SwipeToDelete = ({
  onClick,
  onDelete,
  onDeleteConfirm,
  deleteComponent,
  disabled = false,
  height = 50,
  transitionDuration = 250,
  deleteWidth = 100,
  deleteThreshold = 100,
  showDeleteAction = true,
  deleteColor = "#CC0900",
  deleteText = "Delete",
  className = "",
  id = "",
  rtl = false,
  children,
}: Props) => {
  const [touching, setTouching] = useState(false);
  const [translate, setTranslate] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const startTouchPosition = useRef(0);
  const initTranslate = useRef(0);
  const container = useRef<HTMLDivElement>(null);
  const containerWidth: number = container.current?.getBoundingClientRect().width || 0;
  const deleteWithoutConfirmThreshold: number = containerWidth * (deleteThreshold / 100);

  const onStart = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (disabled) return;
      if (touching) return;
      startTouchPosition.current = cursorPosition(event);
      initTranslate.current = translate;
      setTouching(true);
    },
    [disabled, touching, translate]
  );

  useEffect(() => {
    const root = container.current;
    root?.style.setProperty("--rstdiHeight", height + "px");
    root?.style.setProperty("--rstdiTransitionDuration", transitionDuration + "ms");
    root?.style.setProperty("--rstdiIsRtl", rtl ? "1" : "-1");
    root?.style.setProperty("--rstdiDeleteColor", deleteColor);
    root?.style.setProperty("--rstdiDeleteWidth", deleteWidth + "px");
    root?.style.setProperty("--rstdi-delete-btn-height", height - 2 + "px");
  }, [deleteColor, deleteWidth, height, rtl, transitionDuration]);

  useEffect(() => {
    const root = container.current;
    root?.style.setProperty("--rstdiTranslate", translate * (rtl ? -1 : 1) + "px");
    const shiftDelete = -translate >= deleteWithoutConfirmThreshold;
    root?.style.setProperty(
      `--rstdiButtonMargin${rtl ? "Right" : "Left"}`,
      (shiftDelete ? containerWidth + translate : containerWidth - deleteWidth) + "px"
    );
    if (translate) root?.style.setProperty("--history-item-bg", "#F6F6F6");
    else root?.style.setProperty("--history-item-bg", colors.neutral.white);
  }, [translate, deleteWidth, containerWidth, rtl, deleteWithoutConfirmThreshold]);

  const onMove = useCallback(
    function (event: TouchEvent | MouseEvent) {
      if (!touching) return;
      if (!rtl && cursorPosition(event) > startTouchPosition.current - initTranslate.current) return setTranslate(0);
      if (rtl && cursorPosition(event) < startTouchPosition.current - initTranslate.current) return setTranslate(0);
      setTranslate(cursorPosition(event) - startTouchPosition.current + initTranslate.current);
    },
    [rtl, touching]
  );

  const onMouseMove = useCallback(
    function (event: MouseEvent): any {
      onMove(event);
    },
    [onMove]
  );

  const onTouchMove = useCallback(
    function (event: TouchEvent): any {
      onMove(event);
    },
    [onMove]
  );

  const onDeleteConfirmed = useCallback(() => {
    setDeleting(() => true);
    window.setTimeout(onDelete, transitionDuration);
  }, [onDelete, transitionDuration]);

  const onDeleteCancel = useCallback(() => {
    setTouching(() => false);
    setTranslate(() => 0);
    setDeleting(() => false);
    startTouchPosition.current = 0;
    initTranslate.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDelete, transitionDuration]);

  const onDeleteClick = useCallback(() => {
    if (onDeleteConfirm) {
      onDeleteConfirm(onDeleteConfirmed, onDeleteCancel);
    } else {
      onDeleteConfirmed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDeleteConfirm, onDeleteConfirmed]);

  const onMouseUp = useCallback(
    function () {
      startTouchPosition.current = 0;
      const acceptableMove = -deleteWidth * 0.7;
      const showDelete = showDeleteAction ? (rtl ? -1 : 1) * translate < acceptableMove : false;
      const notShowDelete = showDeleteAction ? (rtl ? -1 : 1) * translate >= acceptableMove : true;
      const deleteWithoutConfirm = (rtl ? 1 : -1) * translate >= deleteWithoutConfirmThreshold;
      if (deleteWithoutConfirm) {
        setTranslate(() => -containerWidth);
      } else if (notShowDelete) {
        setTranslate(() => 0);
      } else if (showDelete && !deleteWithoutConfirm) {
        setTranslate(() => (rtl ? 1 : -1) * deleteWidth);
      }
      setTouching(() => false);
      if (deleteWithoutConfirm) onDeleteClick();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerWidth, deleteWidth, deleteWithoutConfirmThreshold, onDeleteClick, rtl, translate]
  );

  useEffect(() => {
    if (touching) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchend", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchend", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, touching]);

  const [clickFlag, setClickFlag] = useState(true);

  useEffect(() => {
    if (translate) setClickFlag(false);
    else setClickFlag(true);
  }, [translate]);

  return (
    <div
      onClick={() => {
        if (clickFlag) onClick();
      }}
      id={id}
      className={`rstdi${deleting ? " deleting" : ""} ${className}`}
      ref={container}
    >
      <div
        className={`delete${deleting ? " deleting" : ""}`}
        style={{
          backgroundColor: deleting ? "transparent" : deleteColor,
        }}
      >
        <button onClick={onDeleteClick}>
          {/* {deleteComponent ? deleteComponent : deleteText} */}
          DELETE
        </button>
      </div>
      <div
        className={`content${deleting ? " deleting" : ""}${!touching ? " transition" : ""}`}
        onMouseDown={onStart}
        onTouchStart={onStart}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDelete;
